import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { BASIC_CATEGORY_INDEX } from '../packageOptions/packageCategories';
import type { OptionState, PackageOptionId } from '../packageOptions/types';

import { INCLUSION_TYPE_OPTIONS } from './fields';
import type { InclusionFormValues } from './types';

export type PreviousInclusions = {
  drafts: Array<InclusionFormValues>;
  notCopied: number;
  otherCurrencyValue: number;
};

// The tab's PRV is AUD; Salesforce values an inclusion in its own currency.
const PRV_CURRENCY = 'AUD';

type InclusionCell = {
  inclusion: OpportunityRelatedInclusion;
  roomId: PackageOptionId;
  nights: number;
};

// Salesforce repeats an inclusion on every Deal Option it applies to, so identical rows are one inclusion.
function inclusionKey(inclusion: OpportunityRelatedInclusion, name: string): string {
  return [
    inclusion.asset__c ?? '',
    name.toLowerCase(),
    inclusion.type__c ?? '',
    inclusion.currencyisocode ?? '',
    Number(inclusion.rrp__c ?? 0),
  ].join('::');
}

function descriptionOf(inclusion: OpportunityRelatedInclusion): string {
  return String(inclusion.inclusion_description__c ?? '').trim();
}

// Salesforce sometimes leaves the fine print empty on some of the rows it repeats, so a group only splits when
// its rows carry different non-empty fine print; a blank one then stays apart too.
function splitByDescription(cells: Array<InclusionCell>): Array<Array<InclusionCell>> {
  const descriptions = new Set(cells.map(({ inclusion }) => descriptionOf(inclusion)).filter(Boolean));
  if (descriptions.size <= 1) {
    return [cells];
  }
  const cellsByDescription = new Map<string, Array<InclusionCell>>();
  for (const cell of cells) {
    const description = descriptionOf(cell.inclusion);
    cellsByDescription.set(description, [...(cellsByDescription.get(description) ?? []), cell]);
  }
  return [...cellsByDescription.values()];
}

function valueOf(inclusion: OpportunityRelatedInclusion): number | undefined {
  const totalValue = Number(inclusion.total_value__c);
  return totalValue > 0 ? totalValue : undefined;
}

// PRV is the value on the lead package: Salesforce's Total Value on the shortest stay (RRP × nights when Daily).
function toDraft(cells: Array<InclusionCell>, option: OptionState): InclusionFormValues {
  const lead = cells.reduce((shortest, cell) => (cell.nights < shortest.nights ? cell : shortest));
  const { inclusion } = lead;
  const name = String(inclusion.name).trim();
  const description = [lead, ...cells].map((cell) => descriptionOf(cell.inclusion)).find(Boolean);
  const roomIds = option.roomsByCategory[BASIC_CATEGORY_INDEX].map((room) => room.id).filter((id) =>
    cells.some((cell) => cell.roomId === id),
  );
  const los = option.lengthsOfStay.filter((nights) => cells.some((cell) => cell.nights === nights));

  return {
    pricingOptionIndex: 0,
    name,
    description: description ?? '',
    type: INCLUSION_TYPE_OPTIONS.find((type) => type === inclusion.type__c),
    validFor: [],
    prv: inclusion.currencyisocode === PRV_CURRENCY ? valueOf(inclusion) : undefined,
    assetLink: inclusion.asset__c
      ? {
          mode: 'existing',
          existingAssetSfId: inclusion.asset__c,
          existingAssetName: inclusion.asset__name ?? inclusion.asset?.name ?? undefined,
        }
      : { mode: 'new', newAssetName: name },
    applyTo: {
      roomTypes: roomIds.length === option.roomsByCategory[BASIC_CATEGORY_INDEX].length ? 'all' : roomIds,
      occupancies: 'all',
      los: los.length === option.lengthsOfStay.length ? 'all' : los,
    },
  };
}

// Applies-to is rooms × LOS, so rooms covered on different stay lengths become one draft per LOS set.
function toDrafts(cells: Array<InclusionCell>, option: OptionState): Array<InclusionFormValues> {
  const nightsByRoom = new Map<PackageOptionId, Set<number>>();
  for (const { roomId, nights } of cells) {
    nightsByRoom.set(roomId, (nightsByRoom.get(roomId) ?? new Set()).add(nights));
  }
  const roomsByLosSet = new Map<string, Set<PackageOptionId>>();
  for (const [roomId, nights] of nightsByRoom) {
    const losSet = [...nights].sort((a, b) => a - b).join(',');
    roomsByLosSet.set(losSet, (roomsByLosSet.get(losSet) ?? new Set()).add(roomId));
  }
  return [...roomsByLosSet.values()].map((roomIds) =>
    toDraft(
      cells.filter((cell) => roomIds.has(cell.roomId)),
      option,
    ),
  );
}

// Inclusions attach to Option 1's rooms by id, so they are read against the option the Deal Options copy
// produces. A Deal Option that copy leaves out (bundled, no room, several for one room and LOS) takes its
// inclusions with it, since they may belong to a variant rather than the base package.
export function reducePreviousInclusions(
  inclusions: Array<OpportunityRelatedInclusion> = [],
  deals: Array<OpportunityRelatedDeal> = [],
  copiedDealIds: Array<string>,
  option: OptionState,
): PreviousInclusions {
  const copied = new Set(copiedDealIds);
  const dealsById = new Map(deals.filter((deal) => copied.has(String(deal.id))).map((deal) => [String(deal.id), deal]));
  const roomIdByAssetId = new Map(option.roomsByCategory[BASIC_CATEGORY_INDEX].map((room) => [room.assetId, room.id]));
  const cellsByKey = new Map<string, Array<InclusionCell>>();

  for (const inclusion of inclusions) {
    const name = inclusion.name?.trim();
    if (!name) {
      continue;
    }
    const key = inclusionKey(inclusion, name);
    const cells = cellsByKey.get(key) ?? [];
    cellsByKey.set(key, cells);

    const deal = inclusion.deal_offer__c ? dealsById.get(inclusion.deal_offer__c) : undefined;
    const roomId = deal?.room_asset?.sfid ? roomIdByAssetId.get(deal.room_asset.sfid) : undefined;
    if (!deal || !roomId) {
      continue;
    }
    cells.push({ inclusion, roomId, nights: Number(deal.number_of_nights__c) });
  }

  const copiedCells = [...cellsByKey.values()].filter((cells) => cells.length > 0);
  return {
    drafts: copiedCells.flatMap(splitByDescription).flatMap((cells) => toDrafts(cells, option)),
    notCopied: cellsByKey.size - copiedCells.length,
    otherCurrencyValue: copiedCells.filter(
      ([{ inclusion }]) => inclusion.currencyisocode !== PRV_CURRENCY && valueOf(inclusion) !== undefined,
    ).length,
  };
}
